import React, { useRef, useEffect } from 'react';
import { FaStar, FaStarHalfAlt, FaRegStar } from 'react-icons/fa';
import { bookMetrics, openSlotWidth, PLACARD_WIDTH, PLACARD_GAP } from './bookMetrics';

const Stars = ({ rating }) => {
  const label = `Rated ${rating} out of 5`;
  return (
    <span className="placard-stars" role="img" aria-label={label} title={label}>
      {[0, 1, 2, 3, 4].map((i) => {
        if (rating >= i + 1) return <FaStar key={i} />;
        if (rating >= i + 0.5) return <FaStarHalfAlt key={i} />;
        return <FaRegStar key={i} className="placard-star-empty" />;
      })}
      <span className="sr-only">{label}</span>
    </span>
  );
};

const Progress = ({ progress }) => (
  <span className="placard-progress">
    <span className="placard-progress-track">
      <span className="placard-progress-fill" style={{ width: `${Math.max(progress, 1)}%` }} />
    </span>
    <span className="placard-progress-text">{progress}% in</span>
  </span>
);

/**
 * One book on a shelf.
 * mode="spine": stands upright showing its spine; opens by swinging out on a hinge.
 * mode="face":  stands face-out leaning on the back panel; opens by lifting forward.
 */
export default function Book({
  book,
  mode = 'spine',
  index,
  // The shelf stretches spines to fill its width; fall back to the book's own
  // natural thickness when it hasn't been measured yet.
  spineWidth: fittedWidth,
  isOpen,
  onToggle,
  scrollerRef,
}) {
  const slotRef = useRef(null);
  const m = bookMetrics(book);
  const { height, coverWidth, face, palette, shortTitle } = m;
  const spineWidth = fittedWidth ?? m.spineWidth;
  const isReading = typeof book.progress === 'number';
  const isSpine = mode === 'spine';

  // The button is the book itself; the slot is the space it takes on the shelf.
  const bookWidth = isSpine ? spineWidth : coverWidth;
  const openWidth = openSlotWidth(coverWidth);
  const slotWidth = isOpen ? Math.max(bookWidth, openWidth) : bookWidth;

  // When a book opens near the right edge of a scrolling shelf, bring its placard into view.
  useEffect(() => {
    if (!isOpen) return;
    const slot = slotRef.current;
    const scroller = scrollerRef?.current;
    if (!slot || !scroller || typeof scroller.scrollTo !== 'function') return;
    const rightEdge = slot.offsetLeft + openWidth + 24;
    const visibleRight = scroller.scrollLeft + scroller.clientWidth;
    if (rightEdge > visibleRight) {
      scroller.scrollTo({ left: rightEdge - scroller.clientWidth, behavior: 'smooth' });
    }
  }, [isOpen, openWidth, scrollerRef]);

  const style = {
    width: slotWidth,
    height,
    '--i': index,
    '--spine-w': `${spineWidth}px`,
    '--cover-w': `${coverWidth}px`,
    '--cover-h': `${height}px`,
    '--placard-w': `${PLACARD_WIDTH}px`,
    '--placard-gap': `${PLACARD_GAP}px`,
  };

  const slotClass = ['book-slot', `book-slot--${mode}`, isOpen ? 'is-open' : '']
    .filter(Boolean)
    .join(' ');

  const paletteClass = `spine-face-${face} spine-c${palette}`;
  const showAuthor = shortTitle.length <= 13;

  return (
    <div ref={slotRef} className={slotClass} style={style}>
      <button
        type="button"
        className="book"
        aria-expanded={isOpen}
        aria-label={`${book.title} by ${book.author}`}
        data-mode={mode}
        onClick={() => onToggle(book)}
        style={{ width: bookWidth, height }}
      >
        {isSpine ? (
          <>
            <span className={`book-spine ${paletteClass}`}>
              <span className={`spine-title${shortTitle.length > 20 ? ' spine-title--long' : ''}`}>
                {shortTitle}
              </span>
              {/* Narrow spines with long titles drop the author, as real jackets do. */}
              {showAuthor && <span className="spine-author">{book.author}</span>}
            </span>
            <span className="book-cover">
              <img src={book.coverImage} alt="" aria-hidden="true" />
            </span>
          </>
        ) : (
          <span className="book-front">
            <img src={book.coverImage} alt="" aria-hidden="true" />
            {isReading && <span className="bookmark-ribbon" aria-hidden="true" />}
          </span>
        )}
      </button>

      <div
        className="book-placard"
        data-testid={`placard-${book.title}`}
        aria-hidden={!isOpen}
      >
        <p className="placard-title">{book.title}</p>
        <p className="placard-author">{book.author}</p>
        {isReading ? <Progress progress={book.progress} /> : <Stars rating={book.rating} />}
        <a
          className="placard-link"
          href={book.goodreadsUrl}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={isOpen ? 0 : -1}
        >
          View on Goodreads
        </a>
      </div>
    </div>
  );
}
